// Public entry: sign in or create a free account. Both go through the hosted
// identity provider (WorkOS AuthKit in real environments); InteractAds never
// handles passwords. Callback failures come back as ?error=CODE.
import { Navigate } from "@tanstack/react-router";
import { LogIn, UserPlus } from "lucide-react";
import { apiUrl } from "@/api/config";
import { useMe } from "@/api/hooks";
import { Button } from "@/ui/button";
import { Callout } from "@/ui/layout";
import { useSearchRecord } from "../common/page";

const LOGIN_ERRORS: Record<string, string> = {
  RATE_LIMITED: "Demasiados intentos de inicio de sesión. Espera un momento e inténtalo de nuevo.",
  INVALID_STATE: "El inicio de sesión expiró o se abrió en otra pestaña. Vuelve a intentarlo.",
  INVALID_CODE: "No pudimos completar el inicio de sesión. Vuelve a intentarlo.",
  EMAIL_VERIFICATION_REQUIRED: "Verifica tu correo electrónico y vuelve a iniciar sesión.",
  REJECTED_EMAIL_UNVERIFIED: "Verifica tu correo electrónico y vuelve a iniciar sesión.",
  REJECTED_ACCOUNT_LINK_REQUIRED:
    "Ya existe una cuenta con ese correo que usa otro método de acceso. Inicia sesión con el método que usaste la primera vez.",
  REJECTED_USER_DISABLED: "Tu cuenta está deshabilitada. Contacta a soporte.",
  MFA_CONTINUATION_UNAVAILABLE:
    "Tu cuenta requiere un paso de verificación que aún no podemos completar aquí. Contacta a soporte.",
  PROVIDER_UNAVAILABLE: "El servicio de inicio de sesión no está disponible en este momento. Inténtalo más tarde.",
};

export function safeNext(next: unknown): string {
  return typeof next === "string" && next.startsWith("/") && !next.startsWith("//") && next.length < 512 && !next.includes("\\") && !/^\/login(?:[/?#]|$)/.test(next) ? next : "/";
}

export function LoginPage() {
  const search = useSearchRecord();
  const { data: me, isLoading } = useMe();
  const error = typeof search.error === "string" ? search.error : undefined;
  const next = safeNext(search.next);
  if (!isLoading && me) return <Navigate to={next} />;
  const start = (fresh=false) => {
    window.location.assign(apiUrl(`/auth/login?returnTo=${encodeURIComponent(next)}${fresh?"&reauth=1":""}`));
  };
  return (
    <main id="main" className="ia-access">
      <section className="ia-access-intro">
        <img src="/brand/interactads-logo-light.svg" alt="InteractAds" className="w-44"/>
        <div className="grid gap-5"><h1>Tus ideas se convierten en anuncios extraordinarios.</h1>
        <p>Creá anuncios interactivos, juegos publicitarios y experiencias digitales con inteligencia artificial. Convertí tus ideas en creativos profesionales, personalizalos con tu marca y llevá tus campañas a otro nivel.</p></div>
        <ol className="ia-access-steps"><li>Describí tu idea.</li><li>Creá tu anuncio.</li><li>Publicalo y medí resultados.</li></ol>
        <p className="text-xs text-muted">Una plataforma para marcas y equipos de cualquier lugar.</p>
      </section>
      <section className="ia-access-entry" aria-label="Acceso a InteractAds">
        <div className="ia-access-example" aria-label="Ejemplo ilustrativo de anuncio interactivo">
          <span>Ideas que invitan a participar</span><p>¿Te miras?</p><div className="ia-access-mirror">Tu marca.<br/>Una experiencia<br/>que conecta.</div><small>Rich Media · Juegos · Encuestas · Video interactivo</small>
        </div>
        <div className="grid gap-3">
        {error?<Callout tone="danger" title="No pudimos iniciar tu sesión">{LOGIN_ERRORS[error]??'Volvé a intentarlo para continuar.'}</Callout>:null}
        <Button variant="primary" size="lg" onClick={()=>start()}><LogIn/> Iniciar sesión</Button>
        <Button variant="secondary" size="lg" onClick={()=>start()}><UserPlus/> Crear cuenta</Button>
        <Button variant="ghost" size="sm" onClick={()=>start(true)}>Ingresar con otra cuenta</Button>
        </div>
      </section>
    </main>
  );
}
